import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams, Link } from 'react-router-dom';
import ReviewTabs from './ReviewTabs';
import { GradeChip } from './DeveloperQuality';

// Show-your-work drill-down for one developer: the exact score arithmetic,
// penalty breakdowns, every finding with its math, and the AI narrative.

const OBV_LABELS = {
  5: '5 · basics', 4: '4 · expected care', 3: '3 · reviewer catch',
  2: '2 · subtle', 1: '1 · expert nuance',
};

function BreakdownTable({ title, data, keyLabel }) {
  const keys = Object.keys(data || {});
  if (!keys.length) return null;
  const sorted = keys.sort((a, b) => (data[b].penalty - data[a].penalty));
  return (
    <div style={{ minWidth: 220 }}>
      <h4 style={{ margin: '0 0 6px' }}>{title}</h4>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #ccc' }}>
            <th style={{ padding: 4, textAlign: 'left' }}>{keyLabel}</th>
            <th style={{ padding: 4, textAlign: 'right' }}>Count</th>
            <th style={{ padding: 4, textAlign: 'right' }}>Penalty</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map(k => (
            <tr key={k} style={{ borderBottom: '1px solid #eee' }}>
              <td style={{ padding: 4 }}>{OBV_LABELS[k] || k}</td>
              <td style={{ padding: 4, textAlign: 'right' }}>{data[k].count}</td>
              <td style={{ padding: 4, textAlign: 'right' }}>{data[k].penalty.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function DeveloperQualityDetail() {
  const [params] = useSearchParams();
  const author = params.get('author') || '';
  const from = params.get('from') || '';
  const to = params.get('to') || '';

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [genBusy, setGenBusy] = useState(false);
  const [genError, setGenError] = useState('');

  useEffect(() => {
    setLoading(true);
    axios.get('/code-review/developer-quality/detail', { params: { author, from, to } })
      .then((res) => {
        setDetail(res.data);
        setSummary(res.data.cached_summary || null);
        setLoading(false);
      })
      .catch((err) => { setError(err.response?.data?.error || err.message); setLoading(false); });
  }, [author, from, to]);

  const generate = useCallback((force) => {
    setGenBusy(true);
    setGenError('');
    axios.post('/code-review/developer-quality/summary', { author, from, to, force })
      .then((res) => { setSummary(res.data.summary); setGenBusy(false); })
      .catch((err) => { setGenError(err.response?.data?.error || err.message); setGenBusy(false); });
  }, [author, from, to]);

  return (
    <div>
      <div className="page-header">
        <h2>Developer Quality — {detail?.author || author}</h2>
        <p>{from} → {to} · Why this developer got this score, finding by finding.</p>
      </div>
      <div className="page-body">
        <ReviewTabs />
        <p><Link to="/code-review/developer-quality">← Back to leaderboard</Link></p>

        {loading && <div className="spinner" />}
        {error && <div className="error-msg">{error}</div>}

        {detail && !loading && (
          <>
            {/* Score card */}
            <div className="card" style={{ padding: '16px 20px', marginBottom: 16, display: 'flex', gap: 32, alignItems: 'center', flexWrap: 'wrap' }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: 42, fontWeight: 800 }}>{detail.score}</div>
                <GradeChip grade={detail.grade} />
                {detail.insufficient_data &&
                  <div style={{ color: '#b08800', fontSize: 12, marginTop: 4 }}>small sample</div>}
              </div>
              <div style={{ fontSize: 13, lineHeight: 1.8 }}>
                <div>
                  <b>{detail.prs}</b> posted PR reviews ({detail.merged_prs} merged,
                  {' '}{detail.abandoned_prs} abandoned) · <b>{detail.findings_total}</b> findings
                  · <b style={{ color: detail.obvious_count ? '#c62828' : undefined }}>{detail.obvious_count}</b> obvious (4–5)
                  · <b>{detail.subtle_count}</b> subtle (1–2)
                  {detail.unclassified_count > 0 && <> · {detail.unclassified_count} unclassified</>}
                </div>
                <div style={{ color: '#666' }}>
                  Total penalty {detail.score_math.penalty_total} ÷ {detail.score_math.prs} PRs
                  = <b>{detail.score_math.penalty_per_pr}</b> penalty/PR →
                  score = 100 / (1 + {detail.score_math.penalty_per_pr} / {detail.score_math.scale})
                  = <b>{detail.score_math.score}</b>
                </div>
                <div style={{ color: '#666' }}>
                  Fixed-after-flag rate:{' '}
                  {detail.acted_upon_rate === null ? '—' : `${Math.round(detail.acted_upon_rate * 100)}%`}
                  {detail.raw_authors.length > 1 &&
                    <> · Raw author spellings: {detail.raw_authors.join(', ')}</>}
                </div>
              </div>
            </div>

            {/* Narrative */}
            <div className="card" style={{ padding: '16px 20px', marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h3 style={{ margin: 0 }}>Why this score (AI summary)</h3>
                <div>
                  {summary && (
                    <button className="btn btn-default" disabled={genBusy}
                      onClick={() => generate(true)} style={{ marginRight: 8 }}>
                      {genBusy ? 'Regenerating…' : 'Regenerate'}
                    </button>
                  )}
                  {!summary && (
                    <button className="btn btn-primary" disabled={genBusy} onClick={() => generate(false)}>
                      {genBusy ? 'Generating…' : 'Generate summary'}
                    </button>
                  )}
                </div>
              </div>
              {genError && <div className="error-msg">{genError}</div>}
              {summary ? (
                <>
                  <div style={{ whiteSpace: 'pre-wrap', marginTop: 12 }}>{summary.summary_md}</div>
                  <p style={{ color: '#888', fontSize: 12, marginTop: 8 }}>
                    AI-generated ({summary.model}, {summary.generated_at}, ${summary.cost_usd.toFixed(4)}) —
                    review before sharing.
                  </p>
                </>
              ) : (
                !genBusy && <p style={{ color: '#888', marginTop: 12 }}>No summary generated yet for this window.</p>
              )}
            </div>

            {/* Breakdowns */}
            <div className="card" style={{ padding: '16px 20px', marginBottom: 16, display: 'flex', gap: 40, flexWrap: 'wrap' }}>
              <BreakdownTable title="By obviousness" data={detail.by_obviousness} keyLabel="Level" />
              <BreakdownTable title="By severity" data={detail.by_severity} keyLabel="Severity" />
              <BreakdownTable title="By skill area" data={detail.by_skill_area} keyLabel="Skill" />
            </div>

            {/* Findings show-your-work */}
            <div className="card" style={{ padding: '16px 20px', marginBottom: 16 }}>
              <h3 style={{ marginTop: 0 }}>Findings ({detail.findings.length}) — sorted by penalty</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #ccc' }}>
                    <th style={{ padding: 6 }}>Finding</th>
                    <th style={{ padding: 6 }}>PR</th>
                    <th style={{ padding: 6 }}>Severity</th>
                    <th style={{ padding: 6 }}>Obviousness</th>
                    <th style={{ padding: 6 }}>Fixed</th>
                    <th style={{ padding: 6 }} title="severity_w × obviousness_w × confidence × fixed × abandoned">Penalty math</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.findings.map((f) => (
                    <tr key={f.finding_id} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: 6, maxWidth: 340 }}>
                        <b>{f.title}</b>
                        <div style={{ color: '#888' }}>{f.file_path}{f.line ? `:${f.line}` : ''}</div>
                        {f.obviousness_rationale &&
                          <div style={{ color: '#666', fontStyle: 'italic' }}>{f.obviousness_rationale}</div>}
                      </td>
                      <td style={{ padding: 6 }}>
                        <a href={f.pr_url} target="_blank" rel="noreferrer">{f.repo}</a>
                        <div style={{ color: '#888', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.pr_title}</div>
                      </td>
                      <td style={{ padding: 6 }}>{f.severity}</td>
                      <td style={{ padding: 6 }}>
                        {f.obviousness ? OBV_LABELS[f.obviousness] : <i style={{ color: '#b08800' }}>unclassified</i>}
                        {f.skill_area && <div style={{ color: '#888' }}>{f.skill_area}</div>}
                      </td>
                      <td style={{ padding: 6 }}>{f.acted_upon ? '✔' : '—'}</td>
                      <td style={{ padding: 6, whiteSpace: 'nowrap' }}>
                        {f.severity_w} × {f.obviousness_w} × {f.confidence}
                        {f.acted_upon_w !== 1 && ` × ${f.acted_upon_w}`}
                        {f.abandoned_w !== 1 && ` × ${f.abandoned_w}`}
                        {' '}= <b>{f.penalty}</b>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* PRs */}
            <div className="card" style={{ padding: '16px 20px' }}>
              <h3 style={{ marginTop: 0 }}>PRs in window ({detail.prs_list.length})</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #ccc' }}>
                    <th style={{ padding: 6 }}>PR</th>
                    <th style={{ padding: 6 }}>Repo</th>
                    <th style={{ padding: 6 }}>Status</th>
                    <th style={{ padding: 6 }}>Files</th>
                    <th style={{ padding: 6 }}>Findings</th>
                    <th style={{ padding: 6 }}>Penalty</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.prs_list.map((p, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: 6, maxWidth: 320 }}>
                        <a href={p.pr_url} target="_blank" rel="noreferrer">#{p.pr_id}</a>{' '}
                        <span style={{ color: '#666' }}>{p.pr_title}</span>
                      </td>
                      <td style={{ padding: 6 }}>{p.repo}</td>
                      <td style={{ padding: 6 }}>{p.lifecycle_status}</td>
                      <td style={{ padding: 6 }}>{p.changed_files}</td>
                      <td style={{ padding: 6 }}>{p.findings}</td>
                      <td style={{ padding: 6 }}>{p.penalty.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
